import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FiSearch, FiCheck, FiXCircle, FiUser, FiChevronDown,
  FiChevronUp, FiBook, FiArrowRight, FiRefreshCw,
} from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';
import { RelationshipMemoryViewer } from './index';

/**
 * CandidateSurfacingPanel - Handles IN_REVIEW and APPROVED states.
 *
 * Allows staff to:
 * - Run candidate surfacing algorithm
 * - Browse and compare candidates
 * - Select a candidate as target (transitions to STAGED)
 * - Skip candidates with reasons
 * - View relationship memory for candidates
 */

const getErrorMessage = (err, defaultMsg) => {
  if (err.response?.data) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
  }
  return defaultMsg;
};

const CandidateSurfacingPanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  // Candidates state
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [surfacingLoading, setSurfacingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Candidate interaction state
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [selectingCandidate, setSelectingCandidate] = useState(null);
  const [showConfirmSelect, setShowConfirmSelect] = useState(null);
  const [skipCandidate, setSkipCandidate] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [skippingId, setSkippingId] = useState(null);

  // Skipped candidates toggle
  const [showSkipped, setShowSkipped] = useState(false);
  const [skippedCandidates, setSkippedCandidates] = useState([]);

  // Memory viewer
  const [memoryTarget, setMemoryTarget] = useState(null);
  const [memoryTargetName, setMemoryTargetName] = useState(null);

  // Transition state
  const [transitionLoading, setTransitionLoading] = useState(false);

  // Fetch stored candidates
  const fetchCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    setError(null);
    try {
      const [activeRes, skippedRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/staff/brokerage/requests/${requestId}/candidates?include_skipped=false`,
          authHeaders()
        ),
        axios.get(
          `${API_URL}/api/staff/brokerage/requests/${requestId}/candidates?include_skipped=true`,
          authHeaders()
        ),
      ]);
      setCandidates(activeRes.data.items || []);
      // Extract only skipped ones
      const all = skippedRes.data.items || [];
      setSkippedCandidates(all.filter((c) => c.candidate_status === 'SKIPPED'));
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load candidates'));
    } finally {
      setCandidatesLoading(false);
    }
  }, [requestId, authHeaders]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Run surfacing algorithm
  const runSurfacing = async () => {
    setSurfacingLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/surface-candidates?limit=20`,
        {},
        authHeaders()
      );
      setSuccessMessage(
        `Found ${res.data.candidates_found} candidates, stored ${res.data.candidates_stored} new matches.`
      );
      await fetchCandidates();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to run candidate surfacing'));
    } finally {
      setSurfacingLoading(false);
    }
  };

  // Select candidate
  const handleSelectCandidate = async (candidatePersonId) => {
    setSelectingCandidate(candidatePersonId);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/select-candidate`,
        {
          candidate_person_id: candidatePersonId,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      setShowConfirmSelect(null);
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: This request was modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, 'Failed to select candidate'));
      }
    } finally {
      setSelectingCandidate(null);
    }
  };

  // Skip candidate
  const handleSkipCandidate = async (candidatePersonId) => {
    if (!skipReason.trim()) {
      setError('Please provide a reason for skipping');
      return;
    }
    setSkippingId(candidatePersonId);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/skip-candidate`,
        {
          candidate_person_id: candidatePersonId,
          skip_reason: skipReason,
        },
        authHeaders()
      );
      setSkipCandidate(null);
      setSkipReason('');
      await fetchCandidates();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to skip candidate'));
    } finally {
      setSkippingId(null);
    }
  };

  // Transition to next status (for APPROVED -> STAGED if needed manually)
  const transitionRequest = async (targetStatus) => {
    setTransitionLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/transition`,
        {
          target_status: targetStatus,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: Modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, `Failed to transition to ${targetStatus}`));
      }
    } finally {
      setTransitionLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-700 bg-green-100';
    if (score >= 0.6) return 'text-blue-700 bg-blue-100';
    if (score >= 0.4) return 'text-yellow-700 bg-yellow-100';
    return 'text-gray-700 bg-gray-100';
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Candidate Matching</h3>
          <p className="text-xs text-gray-500">
            Surface potential targets based on the request criteria. Select one to proceed to outreach.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runSurfacing}
            disabled={surfacingLoading}
            className="px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {surfacingLoading ? (
              <>
                <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                Surfacing...
              </>
            ) : (
              <>
                <FiSearch className="w-3.5 h-3.5" /> Surface Candidates
              </>
            )}
          </button>
          <button
            onClick={fetchCandidates}
            disabled={candidatesLoading}
            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
            title="Refresh candidates"
          >
            <FiRefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMessage}</div>
      )}

      {/* Transition buttons for IN_REVIEW / APPROVED */}
      {requestDetail.valid_transitions && requestDetail.valid_transitions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {requestDetail.valid_transitions.filter(s => !['STAGED'].includes(s)).map((status) => (
            <button
              key={status}
              onClick={() => {
                if (status === 'DECLINED') {
                  const reason = window.prompt('Reason for declining:');
                  if (reason) transitionRequest(status);
                } else if (status === 'WITHDRAWN') {
                  if (window.confirm('Withdraw this request?')) transitionRequest(status);
                } else {
                  transitionRequest(status);
                }
              }}
              disabled={transitionLoading}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                status === 'DECLINED' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                status === 'WITHDRAWN' ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' :
                status === 'APPROVED' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
              } disabled:opacity-50`}
            >
              {status === 'IN_REVIEW' ? '← Back to Review' :
               status === 'TRIAGE' ? '← Back to Triage' :
               `→ ${status}`}
            </button>
          ))}
        </div>
      )}

      {/* Loading state */}
      {candidatesLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-indigo-600 border-t-transparent rounded-full mr-2" />
          <span className="text-sm text-gray-500">Loading candidates...</span>
        </div>
      )}

      {/* Candidate cards */}
      {!candidatesLoading && candidates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FiUser className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm font-medium">No candidates surfaced yet</p>
          <p className="text-xs mt-1">Click "Surface Candidates" to run the matching algorithm.</p>
        </div>
      )}

      {!candidatesLoading && candidates.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</p>
          {candidates.map((candidate) => {
            const isExpanded = expandedCandidate === candidate.exec_person_id;
            const isSelected = candidate.candidate_status === 'SELECTED';

            return (
              <div
                key={candidate.id}
                className={`border rounded-lg overflow-hidden ${
                  isSelected ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                }`}
              >
                {/* Card header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedCandidate(isExpanded ? null : candidate.exec_person_id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 font-semibold text-sm">
                        {(candidate.person_name || 'C')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {candidate.person_name || 'Unknown'}
                        {isSelected && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-200 text-green-800">
                            SELECTED
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {candidate.current_title || 'Unknown title'}
                        {candidate.current_company && ` at ${candidate.current_company}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getScoreColor(candidate.match_score)}`}>
                      {(candidate.match_score * 100).toFixed(0)}%
                    </span>
                    {isExpanded ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-3 space-y-3">
                    {/* Match reasons */}
                    {candidate.match_reasons && candidate.match_reasons.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Match Reasons</p>
                        <div className="flex flex-wrap gap-1">
                          {candidate.match_reasons.map((reason, i) => (
                            <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] rounded-full">
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Details grid */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {candidate.score_compatibility !== undefined && candidate.score_compatibility !== null && (
                        <div>
                          <span className="text-gray-500">Score Compatibility:</span>{' '}
                          <span className="font-medium">{(candidate.score_compatibility * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {candidate.contact_channel && (
                        <div>
                          <span className="text-gray-500">Contact:</span>{' '}
                          <span className="font-medium">{candidate.contact_channel}</span>
                        </div>
                      )}
                      {candidate.warm_intro_paths && candidate.warm_intro_paths.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Warm Intro Paths:</span>{' '}
                          <span className="font-medium">{candidate.warm_intro_paths.length} path(s)</span>
                        </div>
                      )}
                    </div>

                    {/* Teaser */}
                    {candidate.teaser && (
                      <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Teaser</p>
                        <p className="text-xs text-gray-600 italic">"{candidate.teaser}"</p>
                      </div>
                    )}

                    {/* Actions */}
                    {!isSelected && (
                      <div className="flex gap-2 pt-1">
                        {showConfirmSelect === candidate.exec_person_id ? (
                          <div className="flex items-center gap-2 w-full bg-green-50 border border-green-200 rounded-lg p-2">
                            <span className="text-xs text-green-700 flex-1">
                              Select <strong>{candidate.person_name}</strong> as the target? This moves the request to STAGED.
                            </span>
                            <button
                              onClick={() => handleSelectCandidate(candidate.exec_person_id)}
                              disabled={selectingCandidate === candidate.exec_person_id}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {selectingCandidate === candidate.exec_person_id ? 'Selecting...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setShowConfirmSelect(null)}
                              className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : skipCandidate === candidate.exec_person_id ? (
                          <div className="w-full space-y-2">
                            <textarea
                              value={skipReason}
                              onChange={(e) => setSkipReason(e.target.value)}
                              rows={2}
                              className="w-full text-xs border border-gray-300 rounded p-2 focus:ring-1 focus:ring-indigo-500"
                              placeholder="Reason for skipping this candidate..."
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSkipCandidate(candidate.exec_person_id)}
                                disabled={skippingId === candidate.exec_person_id}
                                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                              >
                                {skippingId === candidate.exec_person_id ? 'Skipping...' : 'Confirm Skip'}
                              </button>
                              <button
                                onClick={() => { setSkipCandidate(null); setSkipReason(''); }}
                                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setShowConfirmSelect(candidate.exec_person_id)}
                              className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                            >
                              <FiCheck className="w-3 h-3" /> Select
                            </button>
                            <button
                              onClick={() => setSkipCandidate(candidate.exec_person_id)}
                              className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1"
                            >
                              <FiXCircle className="w-3 h-3" /> Skip
                            </button>
                            <button
                              onClick={() => {
                                setMemoryTarget(candidate.exec_person_id);
                                setMemoryTargetName(candidate.person_name);
                              }}
                              className="px-3 py-1.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-1"
                            >
                              <FiBook className="w-3 h-3" /> Memory
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Skipped Candidates Section */}
      {skippedCandidates.length > 0 && (
        <div>
          <button
            onClick={() => setShowSkipped(!showSkipped)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            {showSkipped ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
            Skipped ({skippedCandidates.length})
          </button>
          {showSkipped && (
            <div className="mt-2 space-y-2">
              {skippedCandidates.map((candidate) => (
                <div key={candidate.id} className="border border-gray-200 rounded-lg p-2 bg-gray-50 opacity-75">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">{candidate.person_name || 'Unknown'}</span>
                    <span className="text-[10px] text-gray-400">
                      {(candidate.match_score * 100).toFixed(0)}% match
                    </span>
                    <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[10px] rounded">SKIPPED</span>
                  </div>
                  {candidate.skip_reason && (
                    <p className="text-[10px] text-gray-500 mt-1 italic">Reason: {candidate.skip_reason}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Memory Viewer Modal */}
      {memoryTarget && (
        <RelationshipMemoryViewer
          execPersonId={memoryTarget}
          execPersonName={memoryTargetName}
          onClose={() => { setMemoryTarget(null); setMemoryTargetName(null); }}
        />
      )}
    </div>
  );
};

export default CandidateSurfacingPanel;
